/**
 * FormSlidePanel — SlidePanel integrated with react-hook-form.
 *
 * Provides:
 *  - RHF lifecycle (defaultValues, field-level + form-level validation on save)
 *  - Native dirty tracking via formState.dirtyFields → close guard
 *  - In-place reset on open transition (no remount, no focus loss)
 *  - Sticky error banner just above the footer
 *  - Save / Cancel footer buttons with loading state
 *  - Optional Freemium disabled-button tooltip
 *  - setProgrammaticValue / setProgrammaticValues for API-driven updates
 *    that do NOT mark the form dirty
 *
 * Usage:
 *   <FormSlidePanel open={open} onClose={close} title="Edit User" onSave={handleSave}>
 *     {formApi => <MyFormFields {...formApi} />}
 *   </FormSlidePanel>
 */
import * as React from "react";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ErrorMessage } from "@/components/Dialog/Error";
import SlidePanel from "./SlidePanel";
import SlidePanelActions from "./SlidePanelActions";
import type {
  FormApi,
  FormSlidePanelProps,
  SlidePanelFooterContext,
} from "./types";
import { useFormApi } from "./useFormApi";
import { useResetOnOpen } from "./useResetOnOpen";
import { extractApiErrorMessage, hasDisplayableError } from "./utils";

// ─── Inner component (inside RHF context, can call hooks) ────────────────────

const FormSlidePanelInner = <TValues extends Record<string, unknown>>({
  open,
  onClose,
  title,
  subtitle,
  size = "md",
  isLoading = false,
  isSubmitting = false,
  saveText,
  cancelText,
  saveDisabled,
  saveDisabledTitle,
  isFreemium,
  disabledSaveUnmodified = false,
  saveButtonTheme = "secondary",
  errorMessage,
  summaryContent,
  className = "",
  testId,
  children,
  setFormRef,
  onSave,
  validate,
  onValidationFailure,
}: FormSlidePanelProps<TValues>) => {
  const { t } = useTranslation();
  const formApi = useFormApi<TValues>(onSave, validate, onValidationFailure);

  useEffect(() => {
    setFormRef?.(formApi);
  }, [formApi, setFormRef]);

  const isDirty = Object.keys(formApi.modified).length > 0;

  const isSaveDisabled =
    !!saveDisabled || isSubmitting || (disabledSaveUnmodified && !isDirty);

  const errorBanner = hasDisplayableError(errorMessage) ? (
    <ErrorMessage
      message={extractApiErrorMessage(
        errorMessage,
        t("slidePanel.errorFallback"),
      )}
    />
  ) : undefined;

  const footerActions = ({ requestClose }: SlidePanelFooterContext) => (
    <SlidePanelActions
      onCancel={requestClose}
      cancelText={cancelText}
      onSave={e => formApi.onSubmit(e)}
      saveText={saveText}
      saveDisabled={isSaveDisabled}
      saveTooltip={saveDisabled ? saveDisabledTitle : ""}
      isFreemium={isFreemium}
      isSubmitting={isSubmitting}
      saveButtonTheme={saveButtonTheme}
    />
  );

  const renderedSummaryContent =
    typeof summaryContent === "function"
      ? summaryContent(formApi as FormApi<TValues>)
      : summaryContent;

  return (
    <SlidePanel
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      size={size}
      isDirty={isDirty}
      isLoading={isLoading}
      footerActions={footerActions}
      errorBanner={errorBanner}
      summaryContent={renderedSummaryContent}
      className={className}
      testId={testId}
    >
      {children(formApi as FormApi<TValues>)}
    </SlidePanel>
  );
};

// ─── Public component ────────────────────────────────────────────────────────

function FormSlidePanel<
  TValues extends Record<string, unknown> = Record<string, unknown>,
>(props: FormSlidePanelProps<TValues>) {
  const methods = useForm<TValues>({
    defaultValues: props.initialValues as never,
    mode: "onBlur",
    reValidateMode: "onChange",
  });

  useResetOnOpen(
    methods,
    props.open,
    props.initialValues as TValues | undefined,
  );

  return (
    <FormProvider {...methods}>
      <FormSlidePanelInner {...props} />
    </FormProvider>
  );
}

export default FormSlidePanel;
